
import { Filter } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { FEATURED_PRODUCTS } from "@/data/products";
import { Badge } from "../ui/badge";

interface FilterMenuProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
}

const categories = Array.from(
  new Set(FEATURED_PRODUCTS.map((product) => product.category))
);

export function FilterMenu({
  open,
  setOpen,
  selectedCategory,
  setSelectedCategory,
}: FilterMenuProps) {
  // Count products in each category
  const categoryCounts = categories.reduce((acc, category) => {
    acc[category] = FEATURED_PRODUCTS.filter(p => p.category === category).length;
    return acc;
  }, {} as Record<string, number>);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={selectedCategory ? "outline" : "ghost"} 
          size="icon" 
          className={`h-8 w-8 shrink-0 relative ${selectedCategory ? "bg-secondary/50" : ""}`}
          aria-label="Filter products"
        >
          <Filter className="h-4 w-4" />
          {selectedCategory && (
            <span className="absolute top-0 right-0 h-2 w-2 bg-primary rounded-full" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[200px] p-2 rounded-xl"
      >
        <DropdownMenuLabel className="font-semibold">Category</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border" />
        <DropdownMenuItem 
          className={`rounded-full cursor-pointer flex items-center justify-between ${!selectedCategory ? "bg-secondary/30" : ""}`}
          onClick={() => setSelectedCategory(null)}
        >
          All Categories
          <Badge variant="secondary" className="ml-2 rounded-full text-xs">
            {FEATURED_PRODUCTS.length}
          </Badge>
        </DropdownMenuItem>
        {categories.map((category) => (
          <DropdownMenuItem 
            key={category}
            className={`rounded-full cursor-pointer flex items-center justify-between ${selectedCategory === category ? "bg-secondary/30" : ""}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
            <Badge variant="secondary" className="ml-2 rounded-full text-xs">
              {categoryCounts[category]}
            </Badge>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
